import { CELLARHOST_URL, STATIC_SERVICE_PATHS } from "@/lib/external-links";
import { serviceOffers } from "@/lib/service-offers";

/** Public offer lock. Prices only from playbooks / live SKU pages. */

export const ideas = [
    {
        title: "We Build Our Own Products",
        body: "We run 7+ of our own production SaaS platforms — we ship and operate real products, not slide decks.",
    },
    {
        title: "Canadian Focus",
        body: "Built for Canadian businesses with PIPEDA, CASL, and AODA alignment from day one.",
    },
    {
        title: "Founder-Led",
        body: "Direct access to the founding engineer on every project — no account managers in between.",
    },
    {
        title: "Scoped Delivery",
        body: "Timelines depend on scope and are agreed in writing at kickoff. We ship in stages rather than promising a fixed number of weeks up front.",
    },
    {
        title: "AI-First",
        body: "AI embedded from day one — automation, agents, and custom workflows, not bolted-on afterthoughts.",
    },
    {
        title: "Long-Term Partnership",
        body: "Post-launch support, maintenance, and feature expansion — we operate what we build.",
    },
];

const included = (label) => ({ label, on: true });
const excluded = (label) => ({ label, on: false });

export const websitePackages = [
    {
        id: "starter",
        name: "Starter",
        price: "$1,997",
        cadence: "one-time",
        monthly: "+ $99/mo care plan",
        audience: "Contractors, restaurants, sole proprietors & startups.",
        cta: "Get Started",
        popular: false,
        icon: "Globe",
        limits: "Template-based. No AI chatbot, CRM, booking, or automation.",
        points: [
            included("Up to 5 pages"),
            included("Mobile responsive"),
            included("Template-based design"),
            included("Contact forms"),
            included("Basic SEO setup"),
            included("Google Business Profile setup"),
            included("Analytics installation"),
            excluded("AI chatbot"),
            excluded("CRM"),
            excluded("Automation"),
        ],
    },
    {
        id: "growth",
        name: "Growth",
        price: "$3,997",
        cadence: "one-time",
        monthly: "+ $199/mo growth plan",
        audience: "Realtors, insurance & mortgage brokers, clinics, service businesses.",
        cta: "Book Discovery Call",
        popular: false,
        icon: "MessageSquare",
        limits: "No email marketing, conversion optimization, or custom AI agents.",
        points: [
            included("Up to 10 pages"),
            included("Custom design"),
            included("Blog setup"),
            included("Local SEO"),
            included("AI chatbot"),
            included("CRM integration"),
            included("Booking system"),
            included("Lead capture"),
            included("Email integration"),
        ],
    },
    {
        id: "pro",
        name: "Pro",
        price: "$6,997",
        cadence: "one-time",
        monthly: "+ $349/mo AI growth plan",
        audience: "Law firms, healthcare clinics, construction & multi-service businesses.",
        cta: "Book Discovery Call",
        popular: true,
        icon: "Workflow",
        limits: "No custom AI agents, voice AI, custom dashboards, or API integrations.",
        points: [
            included("Up to 20 pages"),
            included("Premium custom design"),
            included("Advanced local SEO"),
            included("AI chatbot with lead qualification"),
            included("CRM automation"),
            included("Email marketing setup"),
            included("Lead nurturing"),
            included("Conversion optimization"),
            included("Monthly reporting"),
            included("Priority support"),
        ],
    },
    {
        id: "partner",
        name: "AI Partner",
        price: "$12,000+",
        cadence: "starting",
        monthly: "+ $799/mo & up",
        audience: "Multi-location companies, franchises, established businesses & organizations.",
        cta: "Schedule Strategy Session",
        popular: false,
        icon: "Bot",
        limits: "Scoped after a strategy session. Implementation is quoted, not a fixed page count.",
        points: [
            included("Unlimited pages"),
            included("Custom UI/UX"),
            included("Custom AI agents"),
            included("Voice AI"),
            included("Workflow automation"),
            included("Custom dashboards"),
            included("API integrations"),
            included("Advanced analytics"),
            included("Growth consulting"),
            included("Monthly strategy sessions"),
            included("Dedicated support"),
        ],
    },
];

export const comparisonRows = [
    { feature: "One-time price", values: ["$1,997", "$3,997", "$6,997", "$12,000+"] },
    { feature: "Monthly plan", values: ["$99/mo", "$199/mo", "$349/mo", "$799+/mo"] },
    { feature: "Pages", values: ["Up to 5", "Up to 10", "Up to 20", "Unlimited"] },
    { feature: "Design", values: ["Template", "Custom", "Premium custom", "Custom UI/UX"] },
    { feature: "SEO", values: ["Basic", "On-page + local", "Full local SEO", "SEO strategy"] },
    { feature: "AI chatbot", values: ["—", "✓", "✓ + lead capture", "Custom AI agents"] },
    { feature: "Lead capture", values: ["—", "✓", "✓", "✓"] },
    { feature: "CRM", values: ["—", "Basic", "CRM automation", "Full workflow automation"] },
    { feature: "Booking system", values: ["—", "✓", "✓", "✓"] },
    { feature: "Email marketing", values: ["—", "—", "✓", "✓"] },
    { feature: "Automation", values: ["—", "Basic", "Workflow", "Full workflow"] },
    { feature: "Dashboards", values: ["—", "—", "—", "✓"] },
    { feature: "API integrations", values: ["—", "—", "—", "✓"] },
    { feature: "Analytics", values: ["✓", "✓", "Advanced", "Advanced"] },
    { feature: "Reporting", values: ["Analytics", "Monthly", "Monthly + CRO", "Growth consulting"] },
    { feature: "Support level", values: ["48-hr", "24-hr", "Priority", "Dedicated"] },
    { feature: "Strategy sessions", values: ["—", "—", "—", "Monthly"] },
    { feature: "Custom AI agents", values: ["—", "—", "—", "✓"] },
    { feature: "Voice AI", values: ["—", "—", "—", "✓"] },
    { feature: "Priority support", values: ["—", "—", "✓", "✓"] },
];

export const pricingSteps = [
    {
        title: "Discovery Call",
        body: "Free 20-minute strategy session to map your goals, audience, and the right package.",
    },
    {
        title: "Design & Build",
        body: "Design, development, and revisions — you see a real homepage early. Every site is built for Canadian businesses, with PIPEDA, CASL, and AODA alignment from day one.",
    },
    {
        title: "Launch & Automate",
        body: "Deploy your website with AI chatbot, CRM, and automation wired in.",
    },
    {
        title: "Grow",
        body: "Ongoing optimization, SEO, reporting, and support — month over month.",
    },
];

export const pricingFaq = [
    {
        q: "Do I own my website?",
        a: "Yes — every package is fully owned by you, with no lock-in. Prices are in CAD; applicable taxes extra.",
    },
    {
        q: "What is the monthly care plan?",
        a: "The care plan covers hosting, security, updates, and ongoing support. It keeps your site fast, secure, and converting after launch.",
    },
    {
        q: "How long does a project take?",
        a: "Most sites launch in 2–6 weeks depending on package and scope. We confirm a timeline on your discovery call.",
    },
    {
        q: "Are there long-term contracts?",
        a: "No long-term contracts. The one-time build is yours, and monthly care plans are month-to-month.",
    },
    {
        q: "Which package is right for me?",
        a: "Most clients start with Growth or Pro — the tiers where AI chatbots and lead capture do the heavy lifting. Book a discovery call and we will recommend the right fit.",
    },
];

export const proposalIndustries = [
    "Real Estate",
    "Compliance / Legal",
    "Agriculture",
    "Insurance",
    "Aviation",
    "Healthcare",
    "Construction",
    "Indigenous Organization",
    "Local Service Business",
    "Other",
];

export const proposalBudgets = [
    "Under $2,000 CAD",
    "$2,000 – $5,000 CAD",
    "$5,000 – $10,000 CAD",
    "$10,000 – $25,000 CAD",
    "$25,000+ CAD",
];

export const services = websitePackages.map((pack) => ({
    slug: pack.id,
    icon: pack.icon,
    title: pack.name,
    short: pack.audience,
    href: "/pricing",
    price: pack.price,
    cta: pack.cta,
    features: pack.points.filter((point) => point.on).map((point) => point.label),
    deliverables: pack.points.filter((point) => point.on).map((point) => point.label),
    process: "Discovery call, then design and build, then launch with whatever automation the package includes.",
    limits: pack.limits,
    faq: [],
}));

export const offerTiers = websitePackages.map((pack) => ({
    name: pack.name,
    sku: pack.name,
    price: pack.price,
    cadence: `${pack.cadence} ${pack.monthly}`,
    href: "/pricing",
    cta: pack.cta,
    highlight: pack.popular,
    points: pack.points.filter((point) => point.on).map((point) => point.label).slice(0, 4),
}));

export const carePlans = [
    {
        name: "Care",
        price: "$99",
        cadence: "/mo",
        points: ["Managed hosting", "SSL", "Security updates", "Weekly backups", "48-hour support"],
    },
    {
        name: "Growth",
        price: "$199",
        cadence: "/mo",
        points: ["Everything in Care", "Ongoing SEO", "Content updates", "Monthly reporting", "24-hour support"],
    },
    {
        name: "AI Growth",
        price: "$349",
        cadence: "/mo",
        points: ["Everything in Growth", "AI chatbot tuning", "Lead automation", "CRM automation", "Priority support"],
    },
    {
        name: "Digital Partner",
        price: "$799+",
        cadence: "/mo",
        points: [
            "Everything in AI Growth",
            "Custom AI agents",
            "Workflow automation",
            "Marketing support",
            "Monthly strategy session",
            "Dedicated account management",
        ],
    },
];

export const products = [
    {
        name: "HVAC Recovery",
        status: "Live",
        href: STATIC_SERVICE_PATHS.hvacRecovery,
        problem: "Independent HVAC shops losing missed and after-hours calls.",
        note: "Demo is synthetic.",
    },
    {
        name: "CellarHost",
        status: "Live · sister",
        href: CELLARHOST_URL,
        problem: "Premium estates that need a concierge, not a chatbot.",
        note: "Aurelia is fictional.",
    },
];

export const costDrivers = [
    {
        title: "Scope",
        body: "Number of pages, custom design versus template, booking or e-commerce features, and integrations with your CRM or email.",
    },
    {
        title: "Content and SEO",
        body: "Who writes the copy, how many service and location pages are needed, and whether ongoing SEO is part of the plan.",
    },
    {
        title: "What to prepare",
        body: "Brand assets, photos, service details, domain access and a named approver. Delays in these usually cause delays in launch.",
    },
];

export const navServices = [
    ...serviceOffers.map((offer) => ({
        label: offer.menu,
        description: offer.card,
        href: `/services/${offer.slug}`,
        kind: "app",
    })),
    {
        label: "Sales Management",
        description: "Manage, rebuild, and put AI on the pipeline.",
        href: "/services/sales",
        kind: "app",
    },
    {
        label: "View all services",
        description: "Every offer in one place.",
        href: "/services",
        kind: "app",
    },
];

export const companyPaths = ["/", "/pricing", "/services", "/services/sales", "/products", "/websites", "/privacy", "/terms"];

export function isCompanyPath(pathname) {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    return companyPaths.includes(path) || path.startsWith("/services/");
}

